import { inject, injectable } from 'inversify';

import { AxiosResponse } from 'axios';
import uuid from 'uuid/v4';
import { observable, makeObservable, action } from 'mobx';
import { History } from 'history';
import {
  AbroadBankWithdrawFiatDetails,
  Money,
  PrepareAbroadFiatWithdrawResponse,
  PrepareAlfaBankAccountWithdrawResponse,
  PrepareCardWithdrawResponse,
  PrepareCryptoWithdrawResponse,
  PrepareProviderWalletWithdrawResponse,
  VerificationMethod,
} from '@unitedtraders/utcom-api-sdk';
import { iocNames } from '../iocNames';
import { UtcomApi } from './UtcomApi';
import { I18Service } from './I18Service';

export enum Steps {
  REQUISITES = 'REQUISITES',
  CONFIRMATION = 'CONFIRMATION',
}

export enum ResultModalType {
  BANK = 'BANK',
  CARD = 'CARD',
  PAYEER = 'PAYEER',
  UTT = 'UTT',
  ALFA_BANK = 'ALFA_BANK',
}

interface CardWithdrawOptions {
  isTargetAmountUsed: boolean;
  instrumentId: string | undefined | null;
}

interface PrepareWithdrawResponse {
  preparedId: string;
  verificationMethod: VerificationMethod;
}

@injectable()
export class WithdrawService {
  private requestId = uuid();

  @observable
  public preparedId?: string;

  @observable
  public verificationMethod?: VerificationMethod;

  @observable
  public currentStep = Steps.REQUISITES;

  @observable
  public resultModalType?: ResultModalType;

  constructor(
    @inject(iocNames.history) private history: History,
    @inject(iocNames.utcomApi) private utcomApi: UtcomApi,
    @inject(iocNames.i18nService) private i18nService: I18Service
  ) {
    makeObservable(this);
  }

  prepareProviderWalletWithdraw = async (instrumentId: string, amount: Money, walletId: string) => {
    this.requestId = uuid();
    const { data }: AxiosResponse<PrepareProviderWalletWithdrawResponse> =
      await this.utcomApi.withdrawService.prepareProviderWalletWithdraw({
        providerWalletDetail: { walletId },
        amount,
        requestId: this.requestId,
        locale: this.i18nService.language,
        withdrawInstrumentId: instrumentId,
      });

    this.goToConfirmationStep(data, ResultModalType.PAYEER);
  };

  prepareCardWithdraw = async (
    amount: Money,
    cardNumber: string,
    cardHolderName: string,
    { instrumentId, isTargetAmountUsed }: CardWithdrawOptions
  ) => {
    const { data }: AxiosResponse<PrepareCardWithdrawResponse> =
      await this.utcomApi.withdrawService.prepareCardWithdraw({
        cardDetails: { cardNumber, cardHolderName },
        amount: amount,
        requestId: uuid(),
        locale: this.i18nService.language,
        isTargetAmountUsed,
        withdrawInstrumentId: instrumentId!,
      });

    this.goToConfirmationStep(data, ResultModalType.CARD);
  };

  prepareAlfaBankAccountWithdraw = async (targetAmount: Money, accountNumber: string) => {
    const { data }: AxiosResponse<PrepareAlfaBankAccountWithdrawResponse> =
      await this.utcomApi.withdrawService.prepareAlfaBankAccountWithdraw({
        targetAmount,
        accountNumber,
        locale: this.i18nService.language,
        requestId: uuid(),
        product: 'UTCOM',
      });

    this.goToConfirmationStep(data, ResultModalType.ALFA_BANK);
  };

  prepareUTTWithdraw = async (money: Money, address: string) => {
    const { data }: AxiosResponse<PrepareCryptoWithdrawResponse> =
      await this.utcomApi.withdrawService.prepareCryptoWithdraw({
        money,
        address,
        requestId: uuid(),
        locale: this.i18nService.language,
        // @ts-ignore actually nullable
        addressId: undefined,
      });

    this.goToConfirmationStep(data, ResultModalType.UTT);
  };

  async prepareAbroadBankWithdraw(money: Money, details: AbroadBankWithdrawFiatDetails) {
    const { data }: AxiosResponse<PrepareAbroadFiatWithdrawResponse> =
      await this.utcomApi.withdrawService.prepareAbroadFiatWithdraw({
        money,
        details,
        requestId: uuid(),
        locale: this.i18nService.language,
      });

    this.goToConfirmationStep(data, ResultModalType.BANK);
  }

  confirm = async (code: string) => {
    if (!this.preparedId) {
      throw new Error('Trying to confirm a non-existent withdrawal');
    }

    await this.utcomApi.withdrawService.verifyWithdraw({
      preparedId: this.preparedId,
      verificationCode: code,
    });

    this.history.push('/success');
  };

  @action
  goToConfirmationStep(response: PrepareWithdrawResponse, resultModalType: ResultModalType) {
    this.currentStep = Steps.CONFIRMATION;
    this.preparedId = response.preparedId;
    this.verificationMethod = response.verificationMethod;
    this.resultModalType = resultModalType;
  }

  @action
  reset() {
    this.currentStep = Steps.REQUISITES;
    this.preparedId = undefined;
    this.resultModalType = undefined;
  }
}
